// web-staking-estimator-frontend/src/hooks/useEstimateState.js
import { useState, useRef, useMemo } from "react";
import { computeSig, stripMapImage, toWGS84FromXY } from "../utils/geometry";
import { fmt } from "../utils/formatting";

/**
 * Custom hook to manage estimate state and persistence
 * @param {object} params - Hook parameters
 * @param {object} params.requestInfo - Service request information
 * @param {object} params.selectedPoint - Selected map point
 * @param {object} params.member - Member information
 * @param {string} params.serviceType - Service type (Residential/Commercial/Irrigation)
 * @returns {object} Hook utilities and state
 */
export function useEstimateState({ requestInfo, selectedPoint, member, serviceType }) {
  const [estimate, setEstimate] = useState({
    distanceFeet: null,
    total: null,
    range: { low: null, high: null },
    lvSubtotal: null,
    mvSubtotal: null,
    mapImageDataUrl: null,
  });

  const [estimateId, setEstimateId] = useState(null);
  const [lastSigForId, setLastSigForId] = useState(null);
  const [prettyEstimateId, setPrettyEstimateId] = useState(null);

  const saveRef = useRef({ promise: null, sig: null });

  /**
   * Compute current signature for change detection
   */
  const currentSig = useMemo(
    () => computeSig(requestInfo, selectedPoint, serviceType),
    [
      serviceType,
      requestInfo.svc_phase,
      requestInfo.svc_volts,
      requestInfo.svc_amps,
      requestInfo.svc_hp,
      requestInfo.svc_ohug,
      selectedPoint?.x,
      selectedPoint?.y,
      selectedPoint?.wkid,
    ]
  );

  /**
   * Build POST data for saving estimate
   * @param {string} mapImageDataUrl - Screenshot data URL to include
   */
  const buildPostData = (mapImageDataUrl) => {
    const { lat, lon } = toWGS84FromXY(
      selectedPoint?.x,
      selectedPoint?.y,
      selectedPoint?.wkid || 3857
    );
    return {
      id: estimateId || undefined,
      // member
      first_name: fmt(member.first_name),
      last_name: fmt(member.last_name),
      business_name: fmt(member.business_name),
      street: fmt(member.street),
      city: fmt(member.city),
      state: fmt(member.state),
      zip: fmt(member.zip),
      phone: fmt(member.phone),
      email: fmt(member.email),
      // location
      pin_lat: lat,
      pin_lon: lon,
      // snapshots - use canonical field names for backend
      inputs: {
        serviceType: requestInfo.svc_type,
        phase: requestInfo.svc_phase,
        voltage: requestInfo.svc_volts,
        amps: requestInfo.svc_amps,
        hp: requestInfo.svc_hp,
        ohug: requestInfo.svc_ohug,
        matchedServiceId: requestInfo.matched_service_id,
        latitude: selectedPoint?.latitude,
        longitude: selectedPoint?.longitude,
        x: selectedPoint?.x,
        y: selectedPoint?.y,
        wkid: selectedPoint?.wkid,
        mapImageDataUrl: mapImageDataUrl || null,
      },
      result: {
        ...stripMapImage(estimate),
        mapImageDataUrl: mapImageDataUrl || null,
      },
    };
  };

  /**
   * Ensure estimate is saved to backend
   * Returns the estimate ID and pretty ID
   * @param {string} mapImageDataUrl - Optional screenshot to use (bypasses state)
   */
  const ensureSaved = async (mapImageDataUrl = null) => {
    // If already saved and signature unchanged, return existing ID (no screenshot needed)
    if (estimateId && lastSigForId && lastSigForId === currentSig) {
      return { id: estimateId, prettyId: prettyEstimateId };
    }

    // If currently saving with same signature, wait for that
    if (saveRef.current.promise && saveRef.current.sig === currentSig) {
      return await saveRef.current.promise;
    }

    // For new saves, require screenshot
    const screenshot = mapImageDataUrl || estimate.mapImageDataUrl;
    if (!screenshot) {
      alert("Preparing map snapshot—try again in a moment.");
      throw new Error("Map snapshot not ready");
    }

    const postData = buildPostData(screenshot);
    const promise = (async () => {
      const response = await fetch("/api/estimates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(postData),
      });
      if (!response.ok) throw new Error(`Save failed: HTTP ${response.status}`);

      const data = await response.json();
      const newId = data?.id || estimateId;
      const newPretty = data?.prettyId ?? data?.readable_id ?? null;

      if (data?.id && data.id !== estimateId) {
        setEstimateId(data.id);
      }
      if (newPretty) setPrettyEstimateId(newPretty);

      setLastSigForId(currentSig);
      return { id: newId, prettyId: newPretty };
    })();

    saveRef.current = { promise, sig: currentSig };
    try {
      const result = await promise;
      return result;
    } finally {
      saveRef.current = { promise: null, sig: null };
    }
  };

  /**
   * Check if estimate is ready for actions
   */
  const isEstimateReady = () => {
    return !!(
      estimateId &&
      estimate.mapImageDataUrl &&
      (Number.isFinite(estimate.total) ||
        (Number.isFinite(estimate?.range?.low) &&
          Number.isFinite(estimate?.range?.high)))
    );
  };

  return {
    estimate,
    setEstimate,
    estimateId,
    setEstimateId,
    prettyEstimateId,
    currentSig,
    lastSigForId,
    setLastSigForId,
    ensureSaved,
    isEstimateReady,
  };
}
